import dayjs from 'dayjs';

import { appConfigStore } from 'Models/AppConfigStore';
import { BookingAddOn, hasAddOnsChanged } from './BookingAddOn';
import { getDefaultPassengerInfo, PassengerDetail } from './PassengerDetail';
import { VehicleBookingWizardData } from './VehicleBookingWizardData';

export class PassengerBookingWizardData {
	type: 'passenger';

	// Passenger tickets
	adultTickets: PassengerDetail[];
	childTickets: PassengerDetail[];
	infantTickets: PassengerDetail[];
	passengerDTickets?: PassengerDetail[];
	passengerETickets?: PassengerDetail[];
	passengerFTickets?: PassengerDetail[];
	passengerGTickets?: PassengerDetail[];
	passengerHTickets?: PassengerDetail[];

	// Additional details
	note?: string;

	// Primary booking contact ("name on booking") for walk-on bookings.
	// Captured on the BookingDetails tab when collectPrimaryBookingNameEnabled.
	primaryFirstName?: string;
	primaryLastName?: string;

	// Admin-configured dynamic field values, keyed by DynamicBookingFieldEntity.Id (uuid).
	dynamicFieldValues?: Record<string, string>;

	// Additional options
	departingTripOptions: BookingAddOn[];
	returningTripOptions: BookingAddOn[];

	constructor(data: Partial<PassengerBookingWizardData>) {
		Object.assign(this, data);
		if (data.departingTripOptions) {
			this.departingTripOptions = data.departingTripOptions.map(x => new BookingAddOn(x));
		}
		if (data.returningTripOptions) {
			this.returningTripOptions = data.returningTripOptions.map(x => new BookingAddOn(x));
		}
	}
}

export function getDefaultPassengerWizardData(): PassengerBookingWizardData {
	return new PassengerBookingWizardData({
		type: 'passenger',
		adultTickets: [getDefaultPassengerInfo('A', false)],
		childTickets: [],
		infantTickets: [],
		passengerDTickets: [],
		passengerETickets: [],
		passengerFTickets: [],
		passengerGTickets: [],
		passengerHTickets: [],
		note: '',
		primaryFirstName: '',
		primaryLastName: '',
		dynamicFieldValues: {},
		departingTripOptions: [],
		returningTripOptions: [],
	});
}

export function passengerBookingWizardDataHasChanged(
	newInnerWizardData: PassengerBookingWizardData | VehicleBookingWizardData,
	oldInnerWizardData: PassengerBookingWizardData | VehicleBookingWizardData,
): boolean {
	// Check if passengers have changed
	if (hasPassengersChanged(newInnerWizardData, oldInnerWizardData)) {
		return true;
	}

	// Check if add-ons have changed in departure trip
	if (hasAddOnsChanged(newInnerWizardData.departingTripOptions, oldInnerWizardData.departingTripOptions)) {
		return true;
	}

	// Check if add-ons have changed in return trip
	if (hasAddOnsChanged(newInnerWizardData.returningTripOptions, oldInnerWizardData.returningTripOptions)) {
		return true;
	}

	if (newInnerWizardData.note !== oldInnerWizardData.note) {
		return true;
	}

	// "Name on booking" (primary contact) lives on these fields when passenger info isn't
	// collected, so a booking-name-only edit wouldn't otherwise register as a change.
	const newPrimary = newInnerWizardData as PassengerBookingWizardData;
	const oldPrimary = oldInnerWizardData as PassengerBookingWizardData;
	if (
		(newPrimary.primaryFirstName ?? '') !== (oldPrimary.primaryFirstName ?? '') ||
		(newPrimary.primaryLastName ?? '') !== (oldPrimary.primaryLastName ?? '')
	) {
		return true;
	}

	return false; // changed
}

export function hasPassengersChanged(
	newInnerWizardData: PassengerBookingWizardData | VehicleBookingWizardData,
	oldInnerWizardData: PassengerBookingWizardData | VehicleBookingWizardData,
): boolean {
	const hasAdultTicketsChanged = hasPassengerDetailsChanged(
		newInnerWizardData.adultTickets,
		oldInnerWizardData.adultTickets,
	);
	if (hasAdultTicketsChanged.value) {
		return true;
	}
	const childTickets = hasPassengerDetailsChanged(newInnerWizardData.childTickets, oldInnerWizardData.childTickets);
	if (childTickets.value) {
		return true;
	}
	const infantTickets = hasPassengerDetailsChanged(
		newInnerWizardData.infantTickets,
		oldInnerWizardData.infantTickets,
	);
	if (infantTickets.value) {
		return true;
	}
	const passengerDTickets = hasPassengerDetailsChanged(
		newInnerWizardData.passengerDTickets ?? [],
		oldInnerWizardData.passengerDTickets ?? [],
	);
	if (passengerDTickets.value) {
		return true;
	}
	const passengerETickets = hasPassengerDetailsChanged(
		newInnerWizardData.passengerETickets ?? [],
		oldInnerWizardData.passengerETickets ?? [],
	);
	if (passengerETickets.value) {
		return true;
	}
	const passengerFTickets = hasPassengerDetailsChanged(
		newInnerWizardData.passengerFTickets ?? [],
		oldInnerWizardData.passengerFTickets ?? [],
	);
	if (passengerFTickets.value) {
		return true;
	}
	const passengerGTickets = hasPassengerDetailsChanged(
		newInnerWizardData.passengerGTickets ?? [],
		oldInnerWizardData.passengerGTickets ?? [],
	);
	if (passengerGTickets.value) {
		return true;
	}
	const passengerHTickets = hasPassengerDetailsChanged(
		newInnerWizardData.passengerHTickets ?? [],
		oldInnerWizardData.passengerHTickets ?? [],
	);
	if (passengerHTickets.value) {
		return true;
	}
	return false; // no change
}

/**
 * The ids of the passengers carrying the driver flag, as a comparable string. Sorted, so it does
 * not matter which of them the wizard happened to write first; a list with no driver gives ''.
 */
function driverIds(list: PassengerDetail[]): string {
	return list
		.filter(x => x?.driver === true)
		.map(x => x.id)
		.sort()
		.join(',');
}

function hasPassengerDetailsChanged(list1: PassengerDetail[], list2: PassengerDetail[]) {
	if (list1.length !== list2.length) {
		return { value: true, reason: 'Length of lists are not the same' } as const;
	}

	// Which passenger is the driver is not passenger *info* — it is a property of the booking, it
	// moves without any of the fields below changing, and it is editable while `passengerInfoEnabled`
	// is off. Compared here rather than as one of the comparators below so that handing the driver
	// from an adult to a senior is not rejected as "No changes made to booking".
	if (driverIds(list1) !== driverIds(list2)) {
		return { value: true, reason: 'driver' } as const;
	}

	// Class and seat are properties of the booking, not passenger *info*: they move without any of
	// the info fields below changing, and they are editable while passengerInfoEnabled is off. So they
	// are compared here rather than as one of the comparators below — the same reasoning as the driver
	// flag above. Without this an edit that only moves a passenger to another seat is rejected as
	// "No changes made to booking".
	//
	// Both legs. A passenger holds a fare type and a seat per sailing, in their own pair of fields, and
	// comparing only the departing pair rejected every edit made on the homeward leg alone.
	const seating = (list: PassengerDetail[]) =>
		list
			.map(
				x =>
					`${x.id}:${x.passengerClassId ?? ''}:${x.seatId ?? ''}` +
					`:${x.returningPassengerClassId ?? ''}:${x.returningSeatId ?? ''}`,
			)
			.sort()
			.join(',');

	if (seating(list1) !== seating(list2)) {
		return { value: true, reason: 'seating' } as const;
	}

	if (appConfigStore.passengerInfoEnabled) {
		const comparators = [];
		if (appConfigStore.passengerAddressEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.address,
				reason: 'passengerAddressEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerAgeEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.age,
				reason: 'passengerAgeEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerDateOfBirthEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) =>
					passengerDetail.dateOfBirth ? dayjs(passengerDetail.dateOfBirth).format('YYYY-MM-DD') : null,
				reason: 'passengerDateOfBirthEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerEmailEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.email,
				reason: 'passengerEmailEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerGenderEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.gender,
				reason: 'passengerGenderEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerFirstNameEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.firstName,
				reason: 'passengerFirstNameEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerLastNameEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.lastName,
				reason: 'passengerLastNameEnabled' as const,
			} as const);
		}
		if (appConfigStore.passengerPhoneEnabled) {
			comparators.push({
				transformer: (passengerDetail: PassengerDetail) => passengerDetail.phone,
				reason: 'passengerPhoneEnabled' as const,
			} as const);
		}

		const map1 = new Map(list1.map(item => [item.id, item]));
		const map2 = new Map(list2.map(item => [item.id, item]));

		// Check items in list1
		for (const comparator of comparators) {
			for (const [key, item1] of map1.entries()) {
				if (!map2.has(key)) {
					return { value: true, reason: comparator.reason } as const; // Found an item in list1 not in list2
				}
				const item2 = map2.get(key)!;
				const value1 = comparator.transformer(item1);
				const value2 = comparator.transformer(item2);
				if (value1 !== value2) {
					return { value: true, reason: comparator.reason } as const; // Found a differing item
				}
			}

			// Check items in list2
			for (const [key] of map2.entries()) {
				if (!map1.has(key)) {
					return { value: true, reason: comparator.reason } as const; // Found an item in list2 not in list1
				}
			}
		}
	}

	return { value: false } as const;
}
